import T from 'T'
import { extractUserMeta, toChangeTypeFromValues } from 'utils/activity/buildVersionActivityTree'
import { formatDateStringToFE } from 'utils/date'
import { combineAddressInfo } from 'utils/helper'
import { formatCentsToCurrency } from 'utils/price'

import type { ActivityRow, ChangeRow, RevisionLike, UserMeta } from 'utils/activity/ActivityTreeBuilder'

type AccountVersion = RevisionLike & {
  accountName?: string
  accountNumber?: string
  active?: boolean
  suspended?: boolean
  csrId?: string | number
  csrUser?: { firstName?: string; lastName?: string } | null
  paymentTermId?: string | number
  paymentTermsId?: string | number
  paymentTerm?: { id?: string | number }
  accountBilling?: { paymentTerm?: { id?: string | number } }
}

type AddressVersion = RevisionLike & {
  line1?: string
  addressLine1?: string
  unitNumber?: string
  suite?: string
  city?: string
  state?: string
  stateShortCode?: string
  stateCode?: string
  zipCode?: string
  postalCode?: string
  country?: string
  latitude?: number | string
  longitude?: number | string
  geoLocation?: { coordinates?: number[] }
}

type ContactVersion = RevisionLike & {
  firstName?: string
  lastName?: string
  position?: string
  deactivated?: boolean
  primaryContact?: boolean
}

type TagRefVersion = RevisionLike & { tagName?: string }

type LateFeeVersion = RevisionLike & {
  feeName?: string
  feeRate?: string
  amountPercent?: number
  amountCents?: number
}

type BillingProfileVersion = RevisionLike & { billingProfileName?: string }

type LifecycleEventType = 'CREATED' | 'DEACTIVATION' | 'REACTIVATION'

type LifecycleEventVersion = RevisionLike & {
  eventType?: LifecycleEventType | null
  status?: string | null
  reasonName?: string | null
  competitorName?: string | null
  noteText?: string | null
  effectiveDate?: string | null
  lastFailure?: { at?: string | null; reason?: string | null } | null
}

type Csr = { id?: string | number; csrId?: string | number; firstName?: string; lastName?: string; name?: string }

export type BuildAccountActivityRowsOptions = {
  csrs?: Csr[]
  contactVersionsById?: Record<string, ContactVersion[]>
  paymentTermsMap?: Map<string, string>
  accountTagRefsData?: { tagRefs?: Record<string, TagRefVersion[]> }
  billingProfileVersions?: BillingProfileVersion[]
  accountLateFeeVersionsData?: { lateFees?: Record<string, LateFeeVersion[]> }
  accountLifecycleEventVersionsData?: { lifecycleEvents?: Record<string, LifecycleEventVersion[]> }
}

type RevisionGroup = {
  ts: number
  parentMeta: UserMeta
  children: ChangeRow[]
  tagAdditions?: TagRefVersion[]
  tagRemovals?: TagRefVersion[]
}

// Labels for account fields we want to show
const FIELD_LABELS = {
  accountName: 'Account name',
  accountNumber: 'Account number',
  status: 'Status',
  billingAddress: 'Billing address',
  coordinates: 'Billing address coordinates',
  accountRep: 'Account representative',
  paymentTerms: 'Payment terms',
  contactName: 'Contact name',
  contactPosition: 'Contact position',
  contactStatus: 'Contact status',
  primaryContact: 'Primary contact',
  billingProfile: 'Billing profile',
  lateFee: 'Late fee',
}

// A single Status value out of the active/suspended flags, in precedence order: a deactivated
// account reads 'Deactivated' whether or not it is also suspended, and an account carrying
// neither flag has no status to report.
const deriveAccountStatus = (version?: AccountVersion): string | undefined => {
  if (!version || (version.active === undefined && version.suspended === undefined)) return undefined
  if (version.active === false) return 'Deactivated'
  if (version.suspended === true) return 'Suspended'
  if (version.active === true) return 'Active'
  return undefined
}

const isEmpty = (v: unknown): boolean => v === null || v === undefined || v === ''

// Helpers to extract address line and coordinates from a raw address version
const addressLineFromVersion = (v?: AddressVersion): string | undefined => {
  if (!v) return undefined
  const addressObj = {
    line1: v?.line1 || v?.addressLine1 || '',
    unitNumber: v?.unitNumber || v?.suite || '',
    city: v?.city || '',
    state: v?.state || v?.stateShortCode?.split?.('-')?.[1] || v?.stateCode || '',
    zipCode: v?.zipCode || v?.postalCode || '',
    country: v?.country || '',
  }
  const line = combineAddressInfo(addressObj) as string
  return line || undefined
}

const coordsFromVersion = (v?: AddressVersion): string | undefined => {
  if (!v) return undefined
  const coords = Array.isArray(v?.geoLocation?.coordinates) ? v.geoLocation?.coordinates : undefined
  const latRaw = v?.latitude ?? (coords ? coords[1] : undefined)
  const lngRaw = v?.longitude ?? (coords ? coords[0] : undefined)
  const toNum = (val?: number | string) => (typeof val === 'string' ? parseFloat(val) : val)
  const lat = toNum(latRaw)
  const lng = toNum(lngRaw)
  if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) return undefined
  return `${lat}, ${lng}`
}

// Contact helpers
const deriveContactStatus = (version?: ContactVersion): string | undefined => {
  if (!version || version.deactivated === undefined) return undefined
  return version.deactivated === true ? 'Deactivated' : 'Active'
}

const toYesNo = (v?: boolean): string | undefined => {
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  return undefined
}

const contactNameFromVersion = (v?: ContactVersion): string | undefined => {
  if (!v) return undefined
  const first = v?.firstName || ''
  const last = v?.lastName || ''
  const name = `${first} ${last}`.trim()
  return name || undefined
}

// Late fee helpers
const lateFeeAmountLabel = (v?: LateFeeVersion): string => {
  if (v?.feeRate === 'PERCENTAGE') return `${v?.amountPercent ?? 0}%`
  return formatCentsToCurrency(v?.amountCents ?? 0) as string
}

const lateFeeDisplayFromVersion = (v?: LateFeeVersion): string | undefined => {
  if (!v) return undefined
  const name = v?.feeName
  if (!name) return undefined
  return `${name} (${lateFeeAmountLabel(v)})`
}

// Lifecycle helpers
const LIFECYCLE_EVENT_LABEL: Record<LifecycleEventType, string> = {
  CREATED: T.ACCOUNT_CREATED,
  DEACTIVATION: T.DEACTIVATION,
  REACTIVATION: T.REACTIVATION,
}

const LIFECYCLE_STATUS_LABEL: Record<string, string> = {
  REQUESTED: T.REQUESTED,
  SCHEDULED: T.SCHEDULED,
  COMPLETED: T.COMPLETED,
  CANCELLED: T.CANCELLED,
}

const lifecycleStatusLabel = (status?: string | null): string | undefined => (status ? LIFECYCLE_STATUS_LABEL[status] || status : undefined)

// Oldest first: every section below diffs each snapshot against the one before it.
const byRevThenTimestampAsc = (a: RevisionLike, b: RevisionLike) => {
  const ar = Number(a?.rev ?? 0)
  const br = Number(b?.rev ?? 0)
  if (ar !== br) return ar - br
  const ad = Date.parse(a?.updatedAt || a?.createdAt || '') || 0
  const bd = Date.parse(b?.updatedAt || b?.createdAt || '') || 0
  return ad - bd
}

// Ensure a group exists for a given rev and keep the latest meta by timestamp
const ensureGroup = (groups: Map<number, RevisionGroup>, rev: number, version: RevisionLike): RevisionGroup => {
  const ts = Date.parse(version?.updatedAt || version?.createdAt || '') || 0
  const meta = extractUserMeta(version) as UserMeta
  if (!groups.has(rev)) {
    groups.set(rev, { ts, parentMeta: meta, children: [] })
  } else {
    const g = groups.get(rev) as RevisionGroup
    if (ts > (g.ts || 0)) {
      g.ts = ts
      g.parentMeta = meta
    }
  }
  return groups.get(rev) as RevisionGroup
}

// Public adapter for Account Activity
// addressVersions: array of address version snapshots (optional)
export function buildAccountActivityRows(
  versions: AccountVersion[] = [],
  addressVersions: AddressVersion[] = [],
  options: BuildAccountActivityRowsOptions = {}
): ActivityRow[] {
  const {
    csrs = [],
    contactVersionsById = {},
    paymentTermsMap,
    accountTagRefsData,
    billingProfileVersions = [],
    accountLateFeeVersionsData,
    accountLifecycleEventVersionsData,
  } = options || {}
  const csrMap = new Map<string, string>()
  if (Array.isArray(csrs)) {
    csrs.forEach(csr => {
      const id = csr?.id ?? csr?.csrId
      const first = csr?.firstName || ''
      const last = csr?.lastName || ''
      const name = `${first} ${last}`.trim() || csr?.name || ''
      if (id !== undefined && id !== null) csrMap.set(String(id), name || String(id))
    })
  }
  const ptMap = paymentTermsMap instanceof Map ? paymentTermsMap : new Map<string, string>()
  const groups = new Map<number, RevisionGroup>()

  // 1) Account-driven diffs (name, number, status)
  const acctSorted = [...(Array.isArray(versions) ? versions : [])].sort(byRevThenTimestampAsc)

  acctSorted.forEach((currRaw, i) => {
    const prev: AccountVersion = acctSorted[i - 1] || {}
    const curr: AccountVersion = currRaw || {}
    const rev = Number(curr?.rev ?? i)
    if (Number.isNaN(rev)) return
    const group = ensureGroup(groups, rev, curr)

    // Account name
    const prevName = prev?.accountName
    const currName = curr?.accountName
    if (!(isEmpty(prevName) && isEmpty(currName)) && prevName !== currName) {
      const changeType = toChangeTypeFromValues(prevName, currName) as string
      group.children.push({
        field: FIELD_LABELS.accountName,
        fieldKey: 'accountName',
        oldValue: isEmpty(prevName) ? 'None' : prevName,
        newValue: isEmpty(currName) ? 'None' : currName,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }

    // Account number
    const prevNum = prev?.accountNumber
    const currNum = curr?.accountNumber
    if (!(isEmpty(prevNum) && isEmpty(currNum)) && prevNum !== currNum) {
      const changeType = toChangeTypeFromValues(prevNum, currNum) as string
      group.children.push({
        field: FIELD_LABELS.accountNumber,
        fieldKey: 'accountNumber',
        oldValue: isEmpty(prevNum) ? 'None' : prevNum,
        newValue: isEmpty(currNum) ? 'None' : currNum,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }

    // Unified Status
    const prevStatus = deriveAccountStatus(prev)
    const currStatus = deriveAccountStatus(curr)
    if (!(isEmpty(prevStatus) && isEmpty(currStatus)) && prevStatus !== currStatus) {
      const changeType = toChangeTypeFromValues(prevStatus, currStatus) as string
      group.children.push({
        field: FIELD_LABELS.status,
        fieldKey: 'status',
        oldValue: isEmpty(prevStatus) ? 'None' : prevStatus,
        newValue: isEmpty(currStatus) ? 'None' : currStatus,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }

    // Account representative (CSR)
    const prevCsrId = prev?.csrId
    const currCsrId = curr?.csrId
    const versionCsrName = (v: AccountVersion) => `${v?.csrUser?.firstName || ''} ${v?.csrUser?.lastName || ''}`.trim()
    const prevCsrName = isEmpty(prevCsrId) ? undefined : csrMap.get(String(prevCsrId)) || versionCsrName(prev) || String(prevCsrId)
    const currCsrName = isEmpty(currCsrId) ? undefined : csrMap.get(String(currCsrId)) || versionCsrName(curr) || String(currCsrId)
    if (!(isEmpty(prevCsrId) && isEmpty(currCsrId)) && prevCsrId !== currCsrId) {
      const changeType = toChangeTypeFromValues(prevCsrName, currCsrName) as string
      group.children.push({
        field: FIELD_LABELS.accountRep,
        fieldKey: 'csrId',
        oldValue: isEmpty(prevCsrName) ? 'None' : prevCsrName,
        newValue: isEmpty(currCsrName) ? 'None' : currCsrName,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }

    // Payment terms (id -> label(id))
    const readPaymentTermId = (v?: AccountVersion) =>
      v?.paymentTermId ?? v?.paymentTermsId ?? v?.paymentTerm?.id ?? v?.accountBilling?.paymentTerm?.id ?? undefined
    const prevPtId = readPaymentTermId(prev)
    const currPtId = readPaymentTermId(curr)
    if (!(isEmpty(prevPtId) && isEmpty(currPtId)) && prevPtId !== currPtId) {
      const resolvePt = (id?: string | number) => {
        if (isEmpty(id)) return undefined
        const key = String(id)
        return ptMap.get(key)
      }
      const prevPt = resolvePt(prevPtId)
      const currPt = resolvePt(currPtId)
      const changeType = toChangeTypeFromValues(prevPt, currPt) as string
      group.children.push({
        field: FIELD_LABELS.paymentTerms,
        fieldKey: 'paymentTermId',
        oldValue: isEmpty(prevPt) ? 'None' : prevPt,
        newValue: isEmpty(currPt) ? 'None' : currPt,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }
  })

  // 2) Address-driven diffs (Billing address, Coordinates) using addressVersions directly
  const addrSorted = [...(Array.isArray(addressVersions) ? addressVersions : [])].sort(byRevThenTimestampAsc)

  addrSorted.forEach((currRaw, i) => {
    const prev: AddressVersion = addrSorted[i - 1] || {}
    const curr: AddressVersion = currRaw || {}
    const rev = Number(curr?.rev ?? i)
    if (Number.isNaN(rev)) return
    const group = ensureGroup(groups, rev, curr)

    // Address line diff
    const prevLine = addressLineFromVersion(prev)
    const currLine = addressLineFromVersion(curr)
    if (!(isEmpty(prevLine) && isEmpty(currLine)) && prevLine !== currLine) {
      const changeType = toChangeTypeFromValues(prevLine, currLine) as string
      group.children.push({
        field: FIELD_LABELS.billingAddress,
        fieldKey: 'billingAddress',
        oldValue: isEmpty(prevLine) ? 'None' : prevLine,
        newValue: isEmpty(currLine) ? 'None' : currLine,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }

    // Coordinates diff
    const prevCoords = coordsFromVersion(prev)
    const currCoords = coordsFromVersion(curr)
    if (!(isEmpty(prevCoords) && isEmpty(currCoords)) && prevCoords !== currCoords) {
      const changeType = toChangeTypeFromValues(prevCoords, currCoords) as string
      group.children.push({
        field: FIELD_LABELS.coordinates,
        fieldKey: 'geoLocation',
        oldValue: isEmpty(prevCoords) ? 'None' : prevCoords,
        newValue: isEmpty(currCoords) ? 'None' : currCoords,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }
  })

  // 3) Contact-driven diffs (Name, Position, Status, Primary contact)
  if (contactVersionsById && typeof contactVersionsById === 'object') {
    const entries = Object.entries(contactVersionsById)
    entries.forEach(([contactIdRaw, rawVersions]) => {
      const contactId = String(contactIdRaw)
      const cSorted = [...(Array.isArray(rawVersions) ? rawVersions : [])].sort(byRevThenTimestampAsc)

      cSorted.forEach((currRaw, i) => {
        const prev: ContactVersion = cSorted[i - 1] || {}
        const curr: ContactVersion = currRaw || {}
        const rev = Number(curr?.rev ?? i)
        if (Number.isNaN(rev)) return
        const group = ensureGroup(groups, rev, curr)

        const prevName = contactNameFromVersion(prev)
        const currName = contactNameFromVersion(curr)
        const displayName = (currName || prevName || contactId).toString()

        // If this version represents a contact deletion, surface an explicit, concise change row
        const currRevType = (curr?.revType || '').toString().toUpperCase()
        const isDeleteRev = currRevType === 'DELETE'
        if (isDeleteRev) {
          group.children.push({
            field: `Contact (${displayName})`,
            fieldKey: `contact.${contactId}.deleted`,
            // Keep values empty so renderer shows compact "Contact (Name) deleted"
            oldValue: '',
            newValue: '',
            changeType: 'Deleted',
            rev,
            ...group.parentMeta,
          })
        }

        // Name change
        if (!(isEmpty(prevName) && isEmpty(currName)) && prevName !== currName) {
          const changeType = toChangeTypeFromValues(prevName, currName) as string
          group.children.push({
            field: `${FIELD_LABELS.contactName} (${displayName})`,
            fieldKey: `contact.${contactId}.name`,
            oldValue: isEmpty(prevName) ? 'None' : prevName,
            newValue: isEmpty(currName) ? 'None' : currName,
            changeType,
            rev,
            ...group.parentMeta,
          })
        }

        // Position change
        const prevPos = prev?.position
        const currPos = curr?.position
        if (!(isEmpty(prevPos) && isEmpty(currPos)) && prevPos !== currPos) {
          const changeType = toChangeTypeFromValues(prevPos, currPos) as string
          group.children.push({
            field: `${FIELD_LABELS.contactPosition} (${displayName})`,
            fieldKey: `contact.${contactId}.position`,
            oldValue: isEmpty(prevPos) ? 'None' : prevPos,
            newValue: isEmpty(currPos) ? 'None' : currPos,
            changeType,
            rev,
            ...group.parentMeta,
          })
        }

        // Status change (Active / Deactivated)
        const prevStatus = deriveContactStatus(prev)
        const currStatus = deriveContactStatus(curr)
        // Suppress separate status line when this revision is a hard delete
        if (!isDeleteRev && !(isEmpty(prevStatus) && isEmpty(currStatus)) && prevStatus !== currStatus) {
          const changeType = toChangeTypeFromValues(prevStatus, currStatus) as string
          group.children.push({
            field: `${FIELD_LABELS.contactStatus} (${displayName})`,
            fieldKey: `contact.${contactId}.status`,
            oldValue: isEmpty(prevStatus) ? 'None' : prevStatus,
            newValue: isEmpty(currStatus) ? 'None' : currStatus,
            changeType,
            rev,
            ...group.parentMeta,
          })
        }

        // Primary contact change (Yes / No)
        const prevPrimary = toYesNo(prev?.primaryContact)
        const currPrimary = toYesNo(curr?.primaryContact)
        if (!(isEmpty(prevPrimary) && isEmpty(currPrimary)) && prevPrimary !== currPrimary) {
          const changeType = toChangeTypeFromValues(prevPrimary, currPrimary) as string
          group.children.push({
            field: `${FIELD_LABELS.primaryContact} (${displayName})`,
            fieldKey: `contact.${contactId}.primaryContact`,
            oldValue: isEmpty(prevPrimary) ? 'None' : prevPrimary,
            newValue: isEmpty(currPrimary) ? 'None' : currPrimary,
            changeType,
            rev,
            ...group.parentMeta,
          })
        }
      })
    })
  }

  // 4) Tag-driven diffs (Tag Added / Removed)
  const tagRefs = accountTagRefsData?.tagRefs && typeof accountTagRefsData.tagRefs === 'object' ? accountTagRefsData.tagRefs : {}
  const tagRefEntries = Object.entries(tagRefs)

  tagRefEntries.forEach(([, rawVersions]) => {
    const tSorted = [...(Array.isArray(rawVersions) ? rawVersions : [])].sort(byRevThenTimestampAsc)

    tSorted.forEach((currRaw, i) => {
      const curr: TagRefVersion = currRaw || {}
      const rev = Number(curr?.rev ?? i)
      if (Number.isNaN(rev)) return
      const group = ensureGroup(groups, rev, curr)

      const revType = (curr?.revType || '').toString().toUpperCase()
      const isInsert = revType === 'INSERT' // 0 = insert
      const isDelete = revType === 'DELETE' // 2 = delete

      if (!group.tagAdditions) group.tagAdditions = []
      if (!group.tagRemovals) group.tagRemovals = []

      if (isInsert) {
        group.tagAdditions.push(curr)
      }

      if (isDelete) {
        group.tagRemovals.push(curr)
      }
    })
  })

  // Post-process groups to add aggregated tag rows
  groups.forEach(group => {
    if (group.tagAdditions && group.tagAdditions.length > 0) {
      group.tagAdditions.sort((a, b) => (a.tagName || '').localeCompare(b.tagName || ''))
      group.children.push({
        field: 'Tags',
        fieldKey: 'tagRef',
        oldValue: '',
        newValue: group.tagAdditions,
        changeType: 'added',
        rev: group.tagAdditions[0].rev ?? 0,
        ...group.parentMeta,
      })
    }
    if (group.tagRemovals && group.tagRemovals.length > 0) {
      group.tagRemovals.sort((a, b) => (a.tagName || '').localeCompare(b.tagName || ''))
      group.children.push({
        field: 'Tags',
        fieldKey: 'tagRef',
        oldValue: group.tagRemovals,
        newValue: '',
        changeType: 'removed',
        rev: group.tagRemovals[0].rev ?? 0,
        ...group.parentMeta,
      })
    }
  })

  // 4.5) Late fee diffs (Late fee added / removed). Each link/unlink mints a fresh
  // LateFeeToAccount row, so every version is either an INSERT or a DELETE — never an update.
  const lateFees =
    accountLateFeeVersionsData?.lateFees && typeof accountLateFeeVersionsData.lateFees === 'object'
      ? accountLateFeeVersionsData.lateFees
      : {}
  const lateFeeEntries = Object.entries(lateFees)

  lateFeeEntries.forEach(([linkId, rawVersions]) => {
    const lfSorted = [...(Array.isArray(rawVersions) ? rawVersions : [])].sort(byRevThenTimestampAsc)

    lfSorted.forEach((currRaw, i) => {
      const curr: LateFeeVersion = currRaw || {}
      const rev = Number(curr?.rev ?? i)
      if (Number.isNaN(rev)) return
      const group = ensureGroup(groups, rev, curr)

      const revType = (curr?.revType || '').toString().toUpperCase()
      const display = lateFeeDisplayFromVersion(curr)
      if (!display) return

      if (revType === 'INSERT') {
        group.children.push({
          field: FIELD_LABELS.lateFee,
          fieldKey: `lateFee.${linkId}.added`,
          oldValue: '',
          newValue: display,
          changeType: 'Added',
          rev,
          ...group.parentMeta,
        })
      } else if (revType === 'DELETE') {
        group.children.push({
          field: FIELD_LABELS.lateFee,
          fieldKey: `lateFee.${linkId}.removed`,
          oldValue: display,
          newValue: '',
          changeType: 'Deleted',
          rev,
          ...group.parentMeta,
        })
      }
    })
  })

  // 4.6) Lifecycle events (account created, deactivation, reactivation). The first state carries what was
  // captured; later ones only move the status, e.g. a background deactivation going from Requested to Completed.
  const lifecycleEvents =
    accountLifecycleEventVersionsData?.lifecycleEvents && typeof accountLifecycleEventVersionsData.lifecycleEvents === 'object'
      ? accountLifecycleEventVersionsData.lifecycleEvents
      : {}

  Object.entries(lifecycleEvents).forEach(([eventId, rawVersions]) => {
    const evSorted = [...(Array.isArray(rawVersions) ? rawVersions : [])].sort(byRevThenTimestampAsc)

    evSorted.forEach((currRaw, i) => {
      const curr: LifecycleEventVersion = currRaw || {}
      const prev: LifecycleEventVersion | undefined = evSorted[i - 1]
      const rev = Number(curr?.rev ?? i)
      if (Number.isNaN(rev) || !curr.eventType) return
      const group = ensureGroup(groups, rev, curr)
      const label = LIFECYCLE_EVENT_LABEL[curr.eventType]
      const push = (suffix: string, key: string, oldValue: string, newValue: string, changeType: string) =>
        group.children.push({
          field: `${label} - ${suffix}`,
          fieldKey: `lifecycle.${eventId}.${key}`,
          oldValue,
          newValue,
          changeType,
          rev,
          ...group.parentMeta,
        })

      const status = lifecycleStatusLabel(curr.status)
      // Each failed run of a schedule is its own revision, so every attempt shows, not just the last.
      if (curr.lastFailure?.at && curr.lastFailure.at !== prev?.lastFailure?.at) {
        push(T.FAILED_ATTEMPT, 'failure', '', curr.lastFailure.reason ?? '', 'Added')
      }
      if (!prev) {
        // An account is only created once it is, so its event has no status worth showing.
        if (curr.eventType !== 'CREATED' && status) push(T.STATUS, 'status', '', status, 'Added')
        if (curr.status === 'SCHEDULED' && curr.effectiveDate) {
          push(T.SCHEDULED_FOR, 'scheduledFor', '', formatDateStringToFE(curr.effectiveDate), 'Added')
        }
        const reasonSuffix = curr.eventType === 'DEACTIVATION' ? T.LOSS_REASON : T.WON_REASON
        if (!isEmpty(curr.reasonName)) push(reasonSuffix, 'reason', '', curr.reasonName as string, 'Added')
        if (!isEmpty(curr.competitorName)) push(T.COMPETITOR, 'competitor', '', curr.competitorName as string, 'Added')
        if (!isEmpty(curr.noteText)) push(T.NOTE, 'note', '', curr.noteText as string, 'Added')
        return
      }

      const prevStatus = lifecycleStatusLabel(prev.status)
      if (status && prevStatus !== status) push(T.STATUS, 'status', prevStatus || '', status, 'Updated')
    })
  })

  // 5) Billing profile diffs
  const bpSorted = [...(Array.isArray(billingProfileVersions) ? billingProfileVersions : [])].sort(byRevThenTimestampAsc)

  bpSorted.forEach((currRaw, i) => {
    const prev: BillingProfileVersion = bpSorted[i - 1] || {}
    const curr: BillingProfileVersion = currRaw || {}
    const rev = Number(curr?.rev ?? i)
    if (Number.isNaN(rev)) return
    const group = ensureGroup(groups, rev, curr)

    const prevName = prev?.billingProfileName
    const currName = curr?.billingProfileName
    if (!(isEmpty(prevName) && isEmpty(currName)) && prevName !== currName) {
      let changeType = toChangeTypeFromValues(prevName, currName) as string
      // When the first available version is an UPDATE, respect the revType instead of
      // inferring "Added" from the missing previous snapshot.
      const currRevType = (curr?.revType || '').toString().toUpperCase()
      const isUpdateWithoutPrev = changeType === 'Added' && currRevType === 'UPDATE'
      if (isUpdateWithoutPrev) changeType = 'Updated'
      const previousName = isEmpty(prevName) ? 'None' : prevName
      const oldValue = isUpdateWithoutPrev ? '' : previousName
      group.children.push({
        field: FIELD_LABELS.billingProfile,
        fieldKey: 'billingProfileName',
        oldValue,
        newValue: isEmpty(currName) ? 'None' : currName,
        changeType,
        rev,
        ...group.parentMeta,
      })
    }
  })

  // 6) Build final rows: one per change, sorted by field name and de-duplicated
  const rows: ActivityRow[] = []
  const revsDesc = Array.from(groups.keys()).sort((a, b) => Number(b) - Number(a))

  revsDesc.forEach(rev => {
    const group = groups.get(rev)
    if (!group || !Array.isArray(group.children) || group.children.length === 0) return

    // Deduplicate children per rev by (fieldKey|old|new)
    const seen = new Set<string>()
    const deduped: ChangeRow[] = []
    group.children.forEach(child => {
      const oldValStr = Array.isArray(child.oldValue) ? JSON.stringify(child.oldValue) : child.oldValue
      const newValStr = Array.isArray(child.newValue) ? JSON.stringify(child.newValue) : child.newValue
      const key = `${child.fieldKey}|${String(oldValStr)}|${String(newValStr)}`
      if (seen.has(key)) return
      seen.add(key)
      deduped.push(child)
    })

    // Sort children by field label
    deduped.sort((a, b) => (a.field || '').localeCompare(b.field || '', undefined, { sensitivity: 'base' }))

    // One row per change, matching ActivityTreeBuilder: the batch row above them was tree chrome.
    deduped.forEach((child, idx) => rows.push({ ...child, id: `rev-${rev}-change-${idx}` }))
  })

  return rows
}
